import React from 'react';
import { css } from '@compiled/react';

import Avatar from '@atlaskit/avatar/avatar';
import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import {
	type AGENT_AVATAR_CLIP_PATH as AgentAvatarClipPath,
	AgentAvatar as LegacyAgentAvatar,
} from '@atlaskit/rovo-agent-components/ui/AgentAvatar';
import AiAgentIcon from '@atlaskit/icon/core/ai-agent';
import IconTile from '@atlaskit/icon/icon-tile';
import { token } from '@atlaskit/tokens';

import { type AgentIconType } from '@atlassian/do-not-use-agent-brand/agent-icon-type';
import type { VisualStudioCodePresenceIconProps } from '@atlassian/do-not-use-agent-brand/icons/VisualStudioCodePresenceIcon';
import type { MicrosoftCopilotPresenceIconProps } from '@atlassian/do-not-use-agent-brand/icons/MicrosoftCopilotPresenceIcon';
import type { KiroPresenceIconProps } from '@atlassian/do-not-use-agent-brand/icons/KiroPresenceIcon';
import type { HermesAgentPresenceIconProps } from '@atlassian/do-not-use-agent-brand/icons/HermesAgentPresenceIcon';
import type { GrokPresenceIconProps } from '@atlassian/do-not-use-agent-brand/icons/GrokPresenceIcon';
import type { GeminiPresenceIconProps } from '@atlassian/do-not-use-agent-brand/icons/GeminiPresenceIcon';
import type { DiaPresenceIconProps } from '@atlassian/do-not-use-agent-brand/icons/DiaPresenceIcon';
import type { AntigravityPresenceIconProps } from '@atlassian/do-not-use-agent-brand/icons/AntigravityPresenceIcon';
import type { AmplitudePresenceIconProps } from '@atlassian/do-not-use-agent-brand/icons/AmplitudePresenceIcon';
import type { CanvaPresenceIconProps } from '@atlassian/do-not-use-agent-brand/icons/CanvaPresenceIcon';
import type { ChatGptIconProps } from '@atlassian/do-not-use-agent-brand/icons/ChatGptIcon';
import type { ChatGptPresenceIconProps } from '@atlassian/do-not-use-agent-brand/icons/ChatGptPresenceIcon';
import type { ClaudeIconProps } from '@atlassian/do-not-use-agent-brand/icons/ClaudeIcon';
import type { ClaudePresenceIconProps } from '@atlassian/do-not-use-agent-brand/icons/ClaudePresenceIcon';
import type { CodexIconProps } from '@atlassian/do-not-use-agent-brand/icons/CodexIcon';
import type { CodexPresenceIconProps } from '@atlassian/do-not-use-agent-brand/icons/CodexPresenceIcon';
import type { CortexPresenceIconProps } from '@atlassian/do-not-use-agent-brand/icons/CortexPresenceIcon';
import type { CursorIconProps } from '@atlassian/do-not-use-agent-brand/icons/CursorIcon';
import type { CursorPresenceIconProps } from '@atlassian/do-not-use-agent-brand/icons/CursorPresenceIcon';
import type { DatabricksPresenceIconProps } from '@atlassian/do-not-use-agent-brand/icons/DatabricksPresenceIcon';
import type { DiaIconProps } from '@atlassian/do-not-use-agent-brand/icons/DiaIcon';
import type { ExternalPresenceIconProps } from '@atlassian/do-not-use-agent-brand/icons/ExternalPresenceIcon';
import type { FigmaPresenceIconProps } from '@atlassian/do-not-use-agent-brand/icons/FigmaPresenceIcon';
import type { FirstPartyAgentPresenceIconProps } from '@atlassian/do-not-use-agent-brand/icons/FirstPartyAgentPresenceIcon';
import type { GitHubCopilotIconProps } from '@atlassian/do-not-use-agent-brand/icons/GitHubCopilotIcon';
import type { GitHubCopilotPresenceIconProps } from '@atlassian/do-not-use-agent-brand/icons/GitHubCopilotPresenceIcon';
import type { GrokIconProps } from '@atlassian/do-not-use-agent-brand/icons/GrokIcon';
import type { HermesAgentIconProps } from '@atlassian/do-not-use-agent-brand/icons/HermesAgentIcon';
import type { KiroIconProps } from '@atlassian/do-not-use-agent-brand/icons/KiroIcon';
import type { LovablePresenceIconProps } from '@atlassian/do-not-use-agent-brand/icons/LovablePresenceIcon';
import type { ManusPresenceIconProps } from '@atlassian/do-not-use-agent-brand/icons/ManusPresenceIcon';
import type { OpenAiPresenceIconProps } from '@atlassian/do-not-use-agent-brand/icons/OpenAiPresenceIcon';
import type { OpenCodeIconProps } from '@atlassian/do-not-use-agent-brand/icons/OpenCodeIcon';
import type { ReplitPresenceIconProps } from '@atlassian/do-not-use-agent-brand/icons/ReplitPresenceIcon';
import type {
	RovoPresenceColorMode as RovoPresenceColorModeType,
	RovoPresenceIconProps,
} from '@atlassian/do-not-use-agent-brand/icons/RovoPresenceIcon';
import type { SlackPresenceIconProps } from '@atlassian/do-not-use-agent-brand/icons/SlackPresenceIcon';
import type { VisualStudioCodeIconProps } from '@atlassian/do-not-use-agent-brand/icons/VisualStudioCodeIcon';
import { RovoIcon } from '@atlassian/do-not-use-agent-brand/icons/RovoIcon';
import { PresenceHexOutlineContext } from '@atlassian/do-not-use-agent-brand/presence-hex-artwork';
import type { PresenceHexOutline as PresenceHexOutlineType } from '@atlassian/do-not-use-agent-brand/presence-hex-artwork';
import {
	getAgentBrandTone,
	resolvePresenceOutline,
} from '@atlassian/do-not-use-agent-brand/presence-icon-chrome';

import { LoadableAfterPaint, type LoadableType } from '@confluence/loadable';
import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';

import { useAgentAvatarColorMode } from './useAgentAvatarColorMode.ts';

export type RovoPresenceColorMode = RovoPresenceColorModeType;
export type PresenceHexOutline = PresenceHexOutlineType;

export type AgentAvatarSize = 'xxsmall' | 'small' | 'medium';

/**
 * The Cursor icon inlines a large base64 raster, so it is code-split into its own
 * chunk (`loadable-CursorIcon`) and loaded after paint rather than statically
 * imported — it only renders for the rare `agentIcon === 'cursor'` case, so its
 * payload should not weigh down every chunk that includes this module.
 */
const CursorIcon: LoadableType<CursorIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-CursorIcon" */ '@atlassian/do-not-use-agent-brand/icons/CursorIcon'
			)
		).CursorIcon,
});

const CursorPresenceIcon: LoadableType<CursorPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-CursorPresenceIcon" */ '@atlassian/do-not-use-agent-brand/icons/CursorPresenceIcon'
			)
		).CursorPresenceIcon,
});

const ExternalPresenceIcon: LoadableType<ExternalPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ExternalPresenceIcon" */ '@atlassian/do-not-use-agent-brand/icons/ExternalPresenceIcon'
			)
		).ExternalPresenceIcon,
});

const CanvaPresenceIcon: LoadableType<CanvaPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-CanvaPresenceIcon" */ '@atlassian/do-not-use-agent-brand/icons/CanvaPresenceIcon'
			)
		).CanvaPresenceIcon,
});

/**
 * The Claude, ChatGPT and Figma brand icons only render for the rare
 * `agentIcon === 'claude'` / `'chatgpt'` / `'figma'` cases, so — like the Cursor icon —
 * they are code-split into their own chunks and loaded after paint rather than weighing
 * down every chunk that includes this module.
 */
const ClaudeIcon: LoadableType<ClaudeIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ClaudeIcon" */ '@atlassian/do-not-use-agent-brand/icons/ClaudeIcon'
			)
		).ClaudeIcon,
});

/**
 * Presence tiles render inside LazyAnalyticsDialog (LAZY). LoadablePaint here
 * crashes the byline dialog: "ClaudePresenceIcon [PAINT] has a higher priority
 * of it's parent component LazyAnalyticsDialog[LAZY]". Match Figma/Lovable and
 * load after paint.
 */
const ClaudePresenceIcon: LoadableType<ClaudePresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ClaudePresenceIcon" */ '@atlassian/do-not-use-agent-brand/icons/ClaudePresenceIcon'
			)
		).ClaudePresenceIcon,
});

const ChatGptIcon: LoadableType<ChatGptIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ChatGptIcon" */ '@atlassian/do-not-use-agent-brand/icons/ChatGptIcon'
			)
		).ChatGptIcon,
});

const FigmaPresenceIcon: LoadableType<FigmaPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-FigmaPresenceIcon" */ '@atlassian/do-not-use-agent-brand/icons/FigmaPresenceIcon'
			)
		).FigmaPresenceIcon,
});

const LovablePresenceIcon: LoadableType<LovablePresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-LovablePresenceIcon" */ '@atlassian/do-not-use-agent-brand/icons/LovablePresenceIcon'
			)
		).LovablePresenceIcon,
});

const ReplitPresenceIcon: LoadableType<ReplitPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ReplitPresenceIcon" */ '@atlassian/do-not-use-agent-brand/icons/ReplitPresenceIcon'
			)
		).ReplitPresenceIcon,
});

/**
 * The Dia and Codex icons only render for the rare `agentIcon === 'dia'` / `'codex'`
 * cases, so — like the other brand icons — they are code-split into their own chunks and
 * loaded after paint.
 */
const DiaIcon: LoadableType<DiaIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-DiaIcon" */ '@atlassian/do-not-use-agent-brand/icons/DiaIcon'
			)
		).DiaIcon,
});

const CodexIcon: LoadableType<CodexIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-CodexIcon" */ '@atlassian/do-not-use-agent-brand/icons/CodexIcon'
			)
		).CodexIcon,
});

const CodexPresenceIcon: LoadableType<CodexPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-CodexPresenceIcon" */ '@atlassian/do-not-use-agent-brand/icons/CodexPresenceIcon'
			)
		).CodexPresenceIcon,
});

const RovoPresenceIcon: LoadableType<RovoPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-RovoPresenceIcon" */ '@atlassian/do-not-use-agent-brand/icons/RovoPresenceIcon'
			)
		).RovoPresenceIcon,
});

const GrokIcon: LoadableType<GrokIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-GrokIcon" */ '@atlassian/do-not-use-agent-brand/icons/GrokIcon'
			)
		).GrokIcon,
});

const GitHubCopilotIcon: LoadableType<GitHubCopilotIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-GitHubCopilotIcon" */ '@atlassian/do-not-use-agent-brand/icons/GitHubCopilotIcon'
			)
		).GitHubCopilotIcon,
});

const VisualStudioCodeIcon: LoadableType<VisualStudioCodeIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-VisualStudioCodeIcon" */ '@atlassian/do-not-use-agent-brand/icons/VisualStudioCodeIcon'
			)
		).VisualStudioCodeIcon,
});

const OpenCodeIcon: LoadableType<OpenCodeIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-OpenCodeIcon" */ '@atlassian/do-not-use-agent-brand/icons/OpenCodeIcon'
			)
		).OpenCodeIcon,
});

const HermesAgentIcon: LoadableType<HermesAgentIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-HermesAgentIcon" */ '@atlassian/do-not-use-agent-brand/icons/HermesAgentIcon'
			)
		).HermesAgentIcon,
});

const KiroIcon: LoadableType<KiroIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-KiroIcon" */ '@atlassian/do-not-use-agent-brand/icons/KiroIcon'
			)
		).KiroIcon,
});

const FirstPartyAgentPresenceIcon: LoadableType<FirstPartyAgentPresenceIconProps> =
	LoadableAfterPaint({
		loader: async () =>
			(
				await import(
					/* webpackChunkName: "loadable-FirstPartyAgentPresenceIcon" */ '@atlassian/do-not-use-agent-brand/icons/FirstPartyAgentPresenceIcon'
				)
			).FirstPartyAgentPresenceIcon,
	});

const AmplitudePresenceIcon: LoadableType<AmplitudePresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-AmplitudePresenceIcon" */ '@atlassian/do-not-use-agent-brand/icons/AmplitudePresenceIcon'
			)
		).AmplitudePresenceIcon,
});

const ChatGptPresenceIcon: LoadableType<ChatGptPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ChatGptPresenceIcon" */ '@atlassian/do-not-use-agent-brand/icons/ChatGptPresenceIcon'
			)
		).ChatGptPresenceIcon,
});

const CortexPresenceIcon: LoadableType<CortexPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-CortexPresenceIcon" */ '@atlassian/do-not-use-agent-brand/icons/CortexPresenceIcon'
			)
		).CortexPresenceIcon,
});

const DatabricksPresenceIcon: LoadableType<DatabricksPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-DatabricksPresenceIcon" */ '@atlassian/do-not-use-agent-brand/icons/DatabricksPresenceIcon'
			)
		).DatabricksPresenceIcon,
});

const GitHubCopilotPresenceIcon: LoadableType<GitHubCopilotPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-GitHubCopilotPresenceIcon" */ '@atlassian/do-not-use-agent-brand/icons/GitHubCopilotPresenceIcon'
			)
		).GitHubCopilotPresenceIcon,
});

const ManusPresenceIcon: LoadableType<ManusPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ManusPresenceIcon" */ '@atlassian/do-not-use-agent-brand/icons/ManusPresenceIcon'
			)
		).ManusPresenceIcon,
});

const OpenAiPresenceIcon: LoadableType<OpenAiPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-OpenAiPresenceIcon" */ '@atlassian/do-not-use-agent-brand/icons/OpenAiPresenceIcon'
			)
		).OpenAiPresenceIcon,
});

const SlackPresenceIcon: LoadableType<SlackPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-SlackPresenceIcon" */ '@atlassian/do-not-use-agent-brand/icons/SlackPresenceIcon'
			)
		).SlackPresenceIcon,
});

const AntigravityPresenceIcon: LoadableType<AntigravityPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-AntigravityPresenceIcon" */ '@atlassian/do-not-use-agent-brand/icons/AntigravityPresenceIcon'
			)
		).AntigravityPresenceIcon,
});

const DiaPresenceIcon: LoadableType<DiaPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-DiaPresenceIcon" */ '@atlassian/do-not-use-agent-brand/icons/DiaPresenceIcon'
			)
		).DiaPresenceIcon,
});

const GeminiPresenceIcon: LoadableType<GeminiPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-GeminiPresenceIcon" */ '@atlassian/do-not-use-agent-brand/icons/GeminiPresenceIcon'
			)
		).GeminiPresenceIcon,
});

const GrokPresenceIcon: LoadableType<GrokPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-GrokPresenceIcon" */ '@atlassian/do-not-use-agent-brand/icons/GrokPresenceIcon'
			)
		).GrokPresenceIcon,
});

const HermesAgentPresenceIcon: LoadableType<HermesAgentPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-HermesAgentPresenceIcon" */ '@atlassian/do-not-use-agent-brand/icons/HermesAgentPresenceIcon'
			)
		).HermesAgentPresenceIcon,
});

const KiroPresenceIcon: LoadableType<KiroPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-KiroPresenceIcon" */ '@atlassian/do-not-use-agent-brand/icons/KiroPresenceIcon'
			)
		).KiroPresenceIcon,
});

const MicrosoftCopilotPresenceIcon: LoadableType<MicrosoftCopilotPresenceIconProps> =
	LoadableAfterPaint({
		loader: async () =>
			(
				await import(
					/* webpackChunkName: "loadable-MicrosoftCopilotPresenceIcon" */ '@atlassian/do-not-use-agent-brand/icons/MicrosoftCopilotPresenceIcon'
				)
			).MicrosoftCopilotPresenceIcon,
	});

const VisualStudioCodePresenceIcon: LoadableType<VisualStudioCodePresenceIconProps> =
	LoadableAfterPaint({
		loader: async () =>
			(
				await import(
					/* webpackChunkName: "loadable-VisualStudioCodePresenceIcon" */ '@atlassian/do-not-use-agent-brand/icons/VisualStudioCodePresenceIcon'
				)
			).VisualStudioCodePresenceIcon,
	});

const unidentifiedAgentAvatarBackgroundStyles = css({
	// Scope Tile's default neutral background to the exact Figma value for this avatar only.
	// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage, @atlaskit/design-system/no-unsafe-design-token-usage
	'--ds-background-neutral': '#DDDEE1',
});

const AGENT_AVATAR_CLIP_PATH =
	'polygon(45% 1.33975%, 46.5798% 0.60307%, 48.26352% 0.15192%, 50% 0%, 51.73648% 0.15192%, 53.4202% 0.60307%, 55% 1.33975%, 89.64102% 21.33975%, 91.06889% 22.33956%, 92.30146% 23.57212%, 93.30127% 25%, 94.03794% 26.5798%, 94.48909% 28.26352%, 94.64102% 30%, 94.64102% 70%, 94.48909% 71.73648%, 94.03794% 73.4202%, 93.30127% 75%, 92.30146% 76.42788%, 91.06889% 77.66044%, 89.64102% 78.66025%, 55% 98.66025%, 53.4202% 99.39693%, 51.73648% 99.84808%, 50% 100%, 48.26352% 99.84808%, 46.5798% 99.39693%, 45% 98.66025%, 10.35898% 78.66025%, 8.93111% 77.66044%, 7.69854% 76.42788%, 6.69873% 75%, 5.96206% 73.4202%, 5.51091% 71.73648%, 5.35898% 70%, 5.35898% 30%, 5.51091% 28.26352%, 5.96206% 26.5798%, 6.69873% 25%, 7.69854% 23.57212%, 8.93111% 22.33956%, 10.35898% 21.33975%)' as const satisfies typeof AgentAvatarClipPath;

const styles = cssMap({
	xxsmall: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '16px',
		height: '16px',
		flexShrink: 0,
	},
	compactInlineSpacing: {
		marginInlineStart: token('space.025'),
		marginInlineEnd: token('space.025'),
	},
	small: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '24px',
		height: '24px',
		flexShrink: 0,
		marginInlineStart: token('space.025'),
		marginInlineEnd: token('space.025'),
	},
	medium: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '32px',
		height: '32px',
		flexShrink: 0,
		marginInlineEnd: token('space.025'),
	},
	iconWrapperSmallNoSpacing: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '24px',
		height: '24px',
		flexShrink: 0,
	},
	iconWrapperMediumNoSpacing: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '32px',
		height: '32px',
		flexShrink: 0,
	},
	unidentifiedAgentAvatarSmall: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '24px',
		height: '24px',
		flexShrink: 0,
		marginInlineStart: token('space.025'),
		marginInlineEnd: token('space.025'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		clipPath: AGENT_AVATAR_CLIP_PATH,
	},
	unidentifiedAgentAvatarMedium: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '32px',
		height: '32px',
		flexShrink: 0,
		marginInlineEnd: token('space.025'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		clipPath: AGENT_AVATAR_CLIP_PATH,
	},
	unidentifiedAgentAvatarSmallNoSpacing: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '24px',
		height: '24px',
		flexShrink: 0,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		clipPath: AGENT_AVATAR_CLIP_PATH,
	},
	unidentifiedAgentAvatarMediumNoSpacing: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '32px',
		height: '32px',
		flexShrink: 0,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		clipPath: AGENT_AVATAR_CLIP_PATH,
	},
	brandAgentTile: {
		// Gray hexagon tile behind a brand mark (Figma/Dia/Codex), matching the gray
		// unidentified-agent avatar so brand icons read as the same hexagon shape.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		clipPath: AGENT_AVATAR_CLIP_PATH,
		backgroundColor: token('color.background.neutral'),
	},
	accountAvatarTile: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		clipPath: AGENT_AVATAR_CLIP_PATH,
	},
	accountAvatarSurface: {
		backgroundColor: token('elevation.surface'),
	},
	profileClickableAccountAvatar: {
		transform: 'scale(1.0526315789473684)',
		transformOrigin: 'center',
	},
	profileClickableAccountAvatarTile: {
		transform: 'scale(1.12, 1.1)',
		transformOrigin: 'center',
	},
	// Claude/Codex/Rovo Presence tiles draw Figma's hex + white stroke in SVG.
	// A CSS hex clip would crop that stroke.
	presenceGlyphTile: {
		// Keep inline SVG baseline spacing from shifting the glyph above the avatar's center.
		display: 'flex',
		overflow: 'visible',
	},
	avatarOffsetInlineStart: {
		transform: 'translateX(2px)',
	},
});

const AgentAvatarContainer = ({
	avatarSize,
	hasInlineSpacing,
	children,
}: {
	avatarSize: AgentAvatarSize;
	hasInlineSpacing: boolean;
	children: React.ReactNode;
}): React.JSX.Element => {
	if (avatarSize === 'xxsmall') {
		return (
			<Box xcss={cx(styles.xxsmall, hasInlineSpacing && styles.compactInlineSpacing)}>
				{children}
			</Box>
		);
	}

	if (avatarSize === 'medium' && hasInlineSpacing) {
		return <Box xcss={styles.medium}>{children}</Box>;
	}

	if (avatarSize === 'medium') {
		return <Box xcss={styles.iconWrapperMediumNoSpacing}>{children}</Box>;
	}

	if (hasInlineSpacing) {
		return <Box xcss={styles.small}>{children}</Box>;
	}

	return <Box xcss={styles.iconWrapperSmallNoSpacing}>{children}</Box>;
};

const UnidentifiedAgentAvatar = ({
	avatarSize,
	hasInlineSpacing,
}: {
	avatarSize: AgentAvatarSize;
	hasInlineSpacing: boolean;
}): React.JSX.Element => {
	if (avatarSize === 'xxsmall') {
		// IconTile starts at 20px; compose the compact 16px hexagon with the native small icon.
		return (
			<Box
				testId="unidentified-agent-avatar"
				xcss={cx(
					styles.xxsmall,
					styles.brandAgentTile,
					hasInlineSpacing && styles.compactInlineSpacing,
				)}
			>
				<AiAgentIcon label="" size="small" color={token('color.icon.subtle')} />
			</Box>
		);
	}

	const icon = (
		<span
			data-testid="unidentified-agent-avatar-background"
			css={unidentifiedAgentAvatarBackgroundStyles}
		>
			<IconTile icon={AiAgentIcon} label="" appearance="gray" size={avatarSize} />
		</span>
	);

	if (avatarSize === 'medium' && hasInlineSpacing) {
		return (
			<Box testId="unidentified-agent-avatar" xcss={styles.unidentifiedAgentAvatarMedium}>
				{icon}
			</Box>
		);
	}

	if (avatarSize === 'medium') {
		return (
			<Box testId="unidentified-agent-avatar" xcss={styles.unidentifiedAgentAvatarMediumNoSpacing}>
				{icon}
			</Box>
		);
	}

	if (hasInlineSpacing) {
		return (
			<Box testId="unidentified-agent-avatar" xcss={styles.unidentifiedAgentAvatarSmall}>
				{icon}
			</Box>
		);
	}

	return (
		<Box testId="unidentified-agent-avatar" xcss={styles.unidentifiedAgentAvatarSmallNoSpacing}>
			{icon}
		</Box>
	);
};

/**
 * Renders an agent row's leading icon: the fixed AiAgent/Rovo icon for agents that
 * couldn't be resolved to an account, otherwise an ADS Avatar in a hexagon. The
 * feature gate keeps the deprecated AgentAvatar as the fallback during rollout.
 */
export const AgentRowAvatar = ({
	name,
	agentIcon,
	borderColor: providedBorderColor,
	avatarUrl,
	avatarSize = 'small',
	agentPresenceIconDimensions,
	markSize,
	isOffsetInlineStart = false,
	hasInlineSpacing = true,
	fillProfileClickableFrame = false,
	colorMode: providedColorMode,
	usePresenceArtwork = false,
	presenceTileFill,
	presenceOutline,
}: {
	name?: string;
	agentIcon?: AgentIconType;
	/** Defaults to the surface token when Presence is enabled; use transparent for borderless avatars. */
	borderColor?: string;
	avatarUrl?: string;
	avatarSize?: AgentAvatarSize;
	/** Optional rendered dimensions for agent Presence hex artwork within the avatar container. */
	agentPresenceIconDimensions?: { width: number; height: number };
	markSize?: 16 | 20;
	isOffsetInlineStart?: boolean;
	hasInlineSpacing?: boolean;
	fillProfileClickableFrame?: boolean;
	colorMode?: RovoPresenceColorMode;
	/** Enables Presence artwork independently of the shared avatar-card rollout. */
	usePresenceArtwork?: boolean;
	/** Optional theme-aware fill for Presence tiles that are normally white. */
	presenceTileFill?: string;
	/** Optional outer outline and independent center stroke for refined Presence tiles. */
	presenceOutline?: PresenceHexOutline;
}): React.JSX.Element => {
	let isDualAgentAvatarCardEnabled = false;
	if (expVal('dual_agent_avatar_card', 'isEnabled', false)) {
		isDualAgentAvatarCardEnabled = true;
	}
	const observedColorMode = useAgentAvatarColorMode();
	// Callers that resolve the color mode themselves, such as the presence face pile, keep priority.
	const colorMode =
		providedColorMode ?? (isDualAgentAvatarCardEnabled ? observedColorMode : 'light');
	const borderColor =
		providedBorderColor === undefined && isDualAgentAvatarCardEnabled
			? token('elevation.surface')
			: providedBorderColor;
	// Memoized because it becomes a context value, which would otherwise re-render every tile.
	const resolvedPresenceOutline = React.useMemo(() => {
		const outline =
			presenceOutline ??
			(isDualAgentAvatarCardEnabled
				? resolvePresenceOutline({ tone: getAgentBrandTone(agentIcon), colorMode })
				: undefined);

		return agentPresenceIconDimensions
			? {
					...(outline ?? { width: 0 }),
					svgHeight: agentPresenceIconDimensions.height,
				}
			: outline;
	}, [
		presenceOutline,
		isDualAgentAvatarCardEnabled,
		agentIcon,
		colorMode,
		agentPresenceIconDimensions,
	]);
	const presenceArtworkEnabled = usePresenceArtwork || isDualAgentAvatarCardEnabled;
	const avatarIconSize = { xxsmall: 16, small: 24, medium: 32 }[avatarSize];
	const iconSize = agentPresenceIconDimensions?.width ?? avatarIconSize;
	const iconMarkSize = markSize ?? avatarIconSize;
	const shouldFillProfileClickableFrame = fillProfileClickableFrame && isDualAgentAvatarCardEnabled;
	const presenceTile = (icon: React.JSX.Element): React.JSX.Element => {
		return (
			<AgentAvatarContainer avatarSize={avatarSize} hasInlineSpacing={hasInlineSpacing}>
				<Box xcss={styles.presenceGlyphTile} testId="presence-brand-tile">
					<PresenceHexOutlineContext.Provider value={resolvedPresenceOutline}>
						{icon}
					</PresenceHexOutlineContext.Provider>
				</Box>
			</AgentAvatarContainer>
		);
	};
	const avatar = (() => {
		if (agentIcon === 'ai-agent' && presenceArtworkEnabled) {
			return presenceTile(
				<ExternalPresenceIcon
					label=""
					size={iconSize}
					// Stays borderless unless a caller asks for a ring, so the gated default is skipped.
					borderColor={providedBorderColor}
					useBorderlessArtwork={
						!usePresenceArtwork || expVal('dual_agent_avatar_card', 'isEnabled', false)
					}
				/>,
			);
		}
		if (agentIcon === 'ai-agent') {
			return (
				<UnidentifiedAgentAvatar avatarSize={avatarSize} hasInlineSpacing={hasInlineSpacing} />
			);
		}
		if (agentIcon === 'rovo' && presenceArtworkEnabled) {
			return presenceTile(
				<RovoPresenceIcon
					label=""
					size={iconSize}
					colorMode={colorMode}
					borderColor={borderColor}
					fill={presenceTileFill}
				/>,
			);
		}
		if (agentIcon === 'rovo') {
			return (
				<AgentAvatarContainer avatarSize={avatarSize} hasInlineSpacing={hasInlineSpacing}>
					<RovoIcon label="" size={iconMarkSize} />
				</AgentAvatarContainer>
			);
		}
		if (agentIcon === 'cursor' && presenceArtworkEnabled) {
			return presenceTile(
				<CursorPresenceIcon label="" size={iconSize} borderColor={borderColor} />,
			);
		}
		if (agentIcon === 'cursor') {
			return (
				<AgentAvatarContainer avatarSize={avatarSize} hasInlineSpacing={hasInlineSpacing}>
					<CursorIcon label="" size={iconMarkSize} />
				</AgentAvatarContainer>
			);
		}
		if (agentIcon === 'canva' && presenceArtworkEnabled) {
			return presenceTile(<CanvaPresenceIcon label="" size={iconSize} borderColor={borderColor} />);
		}
		if (agentIcon === 'claude' && presenceArtworkEnabled) {
			return presenceTile(
				<ClaudePresenceIcon label="" size={iconSize} borderColor={borderColor} />,
			);
		}
		if (agentIcon === 'claude') {
			return (
				<Box
					xcss={cx(
						styles[avatarSize],
						avatarSize === 'xxsmall' && hasInlineSpacing && styles.compactInlineSpacing,
					)}
				>
					<ClaudeIcon label="" size={iconMarkSize} />
				</Box>
			);
		}
		if (agentIcon === 'chatgpt' && presenceArtworkEnabled) {
			return presenceTile(
				<ChatGptPresenceIcon label="" size={iconSize} borderColor={borderColor} />,
			);
		}
		if (agentIcon === 'antigravity' && presenceArtworkEnabled) {
			return presenceTile(
				<AntigravityPresenceIcon
					label=""
					size={iconSize}
					borderColor={borderColor}
					fill={presenceTileFill}
				/>,
			);
		}
		if (agentIcon === 'dia' && presenceArtworkEnabled) {
			return presenceTile(<DiaPresenceIcon label="" size={iconSize} borderColor={borderColor} />);
		}
		if (agentIcon === 'gemini' && presenceArtworkEnabled) {
			return presenceTile(
				<GeminiPresenceIcon
					label=""
					size={iconSize}
					borderColor={borderColor}
					fill={presenceTileFill}
				/>,
			);
		}
		if (agentIcon === 'grok' && presenceArtworkEnabled) {
			return presenceTile(<GrokPresenceIcon label="" size={iconSize} borderColor={borderColor} />);
		}
		if (agentIcon === 'hermes' && presenceArtworkEnabled) {
			return presenceTile(
				<HermesAgentPresenceIcon
					label=""
					size={iconSize}
					borderColor={borderColor}
					fill={presenceTileFill}
				/>,
			);
		}
		if (agentIcon === 'kiro' && presenceArtworkEnabled) {
			return presenceTile(<KiroPresenceIcon label="" size={iconSize} borderColor={borderColor} />);
		}
		if (agentIcon === 'microsoft-copilot' && presenceArtworkEnabled) {
			return presenceTile(
				<MicrosoftCopilotPresenceIcon
					label=""
					size={iconSize}
					borderColor={borderColor}
					fill={presenceTileFill}
				/>,
			);
		}
		if (agentIcon === 'vscode' && presenceArtworkEnabled) {
			return presenceTile(
				<VisualStudioCodePresenceIcon
					label=""
					size={iconSize}
					borderColor={borderColor}
					fill={presenceTileFill}
				/>,
			);
		}
		if (agentIcon === 'chatgpt') {
			return (
				<Box
					xcss={cx(
						styles[avatarSize],
						avatarSize === 'xxsmall' && hasInlineSpacing && styles.compactInlineSpacing,
					)}
				>
					<ChatGptIcon label="" size={iconMarkSize} />
				</Box>
			);
		}
		if (agentIcon === 'figma' && presenceArtworkEnabled) {
			return presenceTile(<FigmaPresenceIcon label="" size={iconSize} borderColor={borderColor} />);
		}
		if (agentIcon === 'lovable' && presenceArtworkEnabled) {
			return presenceTile(
				<LovablePresenceIcon label="" size={iconSize} borderColor={borderColor} />,
			);
		}
		if (agentIcon === 'replit' && presenceArtworkEnabled) {
			return presenceTile(
				<ReplitPresenceIcon label="" size={iconSize} borderColor={borderColor} />,
			);
		}
		if (agentIcon === 'dia') {
			return (
				<Box
					xcss={cx(
						styles[avatarSize],
						avatarSize === 'xxsmall' && hasInlineSpacing && styles.compactInlineSpacing,
						styles.brandAgentTile,
					)}
				>
					<DiaIcon label="" size={iconMarkSize} />
				</Box>
			);
		}
		if (agentIcon === 'codex' && presenceArtworkEnabled) {
			return presenceTile(
				<CodexPresenceIcon
					label=""
					size={iconSize}
					colorMode={colorMode}
					borderColor={borderColor}
				/>,
			);
		}
		if (agentIcon === 'first-party' && presenceArtworkEnabled) {
			return presenceTile(
				<FirstPartyAgentPresenceIcon label="" size={iconSize} borderColor={borderColor} />,
			);
		}
		if (agentIcon === 'amplitude' && presenceArtworkEnabled) {
			return presenceTile(
				<AmplitudePresenceIcon label="" size={iconSize} borderColor={borderColor} />,
			);
		}
		if (agentIcon === 'cortex' && presenceArtworkEnabled) {
			return presenceTile(
				<CortexPresenceIcon label="" size={iconSize} borderColor={borderColor} />,
			);
		}
		if (agentIcon === 'databricks' && presenceArtworkEnabled) {
			return presenceTile(
				<DatabricksPresenceIcon label="" size={iconSize} borderColor={borderColor} />,
			);
		}
		if (agentIcon === 'github-copilot' && presenceArtworkEnabled) {
			return presenceTile(
				<GitHubCopilotPresenceIcon label="" size={iconSize} borderColor={borderColor} />,
			);
		}
		if (agentIcon === 'manus' && presenceArtworkEnabled) {
			return presenceTile(<ManusPresenceIcon label="" size={iconSize} borderColor={borderColor} />);
		}
		if (agentIcon === 'openai' && presenceArtworkEnabled) {
			return presenceTile(
				<OpenAiPresenceIcon label="" size={iconSize} borderColor={borderColor} />,
			);
		}
		if (agentIcon === 'slack' && presenceArtworkEnabled) {
			return presenceTile(<SlackPresenceIcon label="" size={iconSize} borderColor={borderColor} />);
		}
		if (agentIcon === 'codex') {
			return (
				<Box
					xcss={cx(
						styles[avatarSize],
						avatarSize === 'xxsmall' && hasInlineSpacing && styles.compactInlineSpacing,
						styles.brandAgentTile,
					)}
				>
					<CodexIcon label="" size={iconMarkSize} />
				</Box>
			);
		}
		if (agentIcon === 'grok' && presenceArtworkEnabled) {
			return (
				<Box
					xcss={cx(
						styles[avatarSize],
						avatarSize === 'xxsmall' && hasInlineSpacing && styles.compactInlineSpacing,
						styles.brandAgentTile,
					)}
				>
					<GrokIcon label="" size={iconMarkSize} />
				</Box>
			);
		}
		if (agentIcon === 'github-copilot' && presenceArtworkEnabled) {
			return (
				<Box
					xcss={cx(
						styles[avatarSize],
						avatarSize === 'xxsmall' && hasInlineSpacing && styles.compactInlineSpacing,
						styles.brandAgentTile,
					)}
				>
					<GitHubCopilotIcon label="" size={iconMarkSize} />
				</Box>
			);
		}
		if (agentIcon === 'vscode' && presenceArtworkEnabled) {
			return (
				<Box
					xcss={cx(
						styles[avatarSize],
						avatarSize === 'xxsmall' && hasInlineSpacing && styles.compactInlineSpacing,
						styles.brandAgentTile,
					)}
				>
					<VisualStudioCodeIcon label="" size={iconMarkSize} />
				</Box>
			);
		}
		if (agentIcon === 'open-code' && presenceArtworkEnabled) {
			return (
				<Box
					xcss={cx(
						styles[avatarSize],
						avatarSize === 'xxsmall' && hasInlineSpacing && styles.compactInlineSpacing,
						styles.brandAgentTile,
					)}
				>
					<OpenCodeIcon label="" size={iconMarkSize} />
				</Box>
			);
		}
		if (agentIcon === 'hermes' && presenceArtworkEnabled) {
			return (
				<Box
					xcss={cx(
						styles[avatarSize],
						avatarSize === 'xxsmall' && hasInlineSpacing && styles.compactInlineSpacing,
						styles.brandAgentTile,
					)}
				>
					<HermesAgentIcon label="" size={iconMarkSize} />
				</Box>
			);
		}
		if (agentIcon === 'kiro' && presenceArtworkEnabled) {
			return (
				<Box
					xcss={cx(
						styles[avatarSize],
						avatarSize === 'xxsmall' && hasInlineSpacing && styles.compactInlineSpacing,
						styles.brandAgentTile,
					)}
				>
					<KiroIcon label="" size={iconMarkSize} />
				</Box>
			);
		}
		const hasDefaultAccountAvatarBorder =
			providedBorderColor === undefined && isDualAgentAvatarCardEnabled;
		const accountAvatar = isDualAgentAvatarCardEnabled ? (
			<Avatar
				appearance="hexagon"
				src={avatarUrl}
				name={name}
				label=""
				borderColor={borderColor ?? token('elevation.surface')}
				size={avatarSize}
				// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
				UNSAFE_isUpdatedGeometry={fg('platform-dst-avatar-updated-geometry')}
			/>
		) : (
			<LegacyAgentAvatar
				size={avatarSize}
				name={name}
				label=""
				imageUrl={avatarUrl}
				showBorder={borderColor === undefined && !hasDefaultAccountAvatarBorder}
				// Swaps to ADS Avatar, which owns the border from here, so `showBorder` stops applying.
				// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
				UNSAFE_useAdsAvatar={fg('platform-dst-avatar-updated-geometry')}
			/>
		);
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		if (fg('platform-dst-avatar-updated-geometry')) {
			// ADS owns the hexagon clip, border, and alignment, so the local clip-path tile cut to the
			// old geometry would shave its corners and the scale corrections would double up.
			// The enlarged hexagon also overflows its nominal box, 25.76px tall at `small`, not 24px.
			return (
				<AgentAvatarContainer avatarSize={avatarSize} hasInlineSpacing={hasInlineSpacing}>
					{accountAvatar}
				</AgentAvatarContainer>
			);
		}
		const sizedAccountAvatar = shouldFillProfileClickableFrame ? (
			<Box
				testId="profile-clickable-agent-avatar-frame"
				xcss={styles.profileClickableAccountAvatar}
			>
				{accountAvatar}
			</Box>
		) : (
			accountAvatar
		);
		if (hasDefaultAccountAvatarBorder) {
			return (
				<AgentAvatarContainer avatarSize={avatarSize} hasInlineSpacing={hasInlineSpacing}>
					<Box
						xcss={cx(
							styles.accountAvatarTile,
							styles.accountAvatarSurface,
							shouldFillProfileClickableFrame && styles.profileClickableAccountAvatarTile,
						)}
					>
						{sizedAccountAvatar}
					</Box>
				</AgentAvatarContainer>
			);
		}
		return (
			<AgentAvatarContainer avatarSize={avatarSize} hasInlineSpacing={hasInlineSpacing}>
				{borderColor === undefined ? (
					sizedAccountAvatar
				) : (
					<Box
						xcss={cx(
							styles.accountAvatarTile,
							shouldFillProfileClickableFrame && styles.profileClickableAccountAvatarTile,
						)}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- providedBorderColor is a runtime value
						style={{ backgroundColor: providedBorderColor }}
					>
						{sizedAccountAvatar}
					</Box>
				)}
			</AgentAvatarContainer>
		);
	})();

	return isOffsetInlineStart ? (
		<Box testId="agent-avatar-offset" xcss={styles.avatarOffsetInlineStart}>
			{avatar}
		</Box>
	) : (
		avatar
	);
};
